import * as React from "react"
import { Eye, Info, Pencil, RefreshCw } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ListEditorSheet } from "@/components/alfred/list-editor-sheet"
import {
  MaskedCredentialInput,
  PROTECTED_CREDENTIAL_MASK,
} from "@/components/shared/masked-credential-input"
import type { ProVenueInstanceInput } from "@/types/alfred-config"

interface ProVenueInstanceEditorProps {
  items: ProVenueInstanceInput[]
  onChange: (next: ProVenueInstanceInput[]) => void
  disabled?: boolean
  triggerAriaLabel?: string
  /**
   * The displayed rows are resolved global metadata, not editable device
   * overrides. Replacing them must start from an empty list because their
   * protected credentials are never exposed to the frontend.
   */
  usesGlobalDefault?: boolean
  /**
   * When true, every instance must carry freshly entered credentials (used for
   * bulk replacement, where secrets cannot be copied from the selected
   * devices). When false, only newly added instances require credentials and
   * blank inputs on existing instances mean "keep current".
   */
  requireSecret?: boolean
}

const INPUT_CLASSES =
  "bg-transparent border-input text-foreground focus:border-ring focus:ring-ring/15 focus:ring-[3px]"
const LABEL_CLASSES = "text-[13px] text-muted-foreground font-medium"

function emptyProVenueInstance(): ProVenueInstanceInput {
  return {
    name: "",
    hostName: "",
    consumerKey: "",
    consumerSecret: "",
  }
}

export function ProVenueInstanceEditor({
  items,
  onChange,
  disabled = false,
  triggerAriaLabel,
  usesGlobalDefault = false,
  requireSecret = false,
}: ProVenueInstanceEditorProps) {
  const [open, setOpen] = React.useState(false)
  const isReplacingGlobalDefault = usesGlobalDefault && !disabled
  const allCredentialsRequired = requireSecret || isReplacingGlobalDefault
  const trigger = (
    <Button
      aria-label={triggerAriaLabel}
      variant="outline"
      size="sm"
      onClick={() => setOpen(true)}
      className="bg-card border-input text-foreground hover:bg-secondary"
    >
      {disabled ? (
        <Eye className="h-4 w-4 mr-2" />
      ) : isReplacingGlobalDefault ? (
        <RefreshCw className="h-4 w-4 mr-2" />
      ) : (
        <Pencil className="h-4 w-4 mr-2" />
      )}
      {disabled ? "View" : isReplacingGlobalDefault ? "Replace All" : "Manage"}
    </Button>
  )

  return (
    <div className="min-w-0 flex-1 space-y-2">
      {isReplacingGlobalDefault ? (
        <Alert className="bg-muted/30 py-3">
          <Info />
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <AlertTitle>Using Global ProVenue Instances</AlertTitle>
              <AlertDescription>
                Instances and credentials are inherited from Global Defaults. To customize them,
                replace the entire list and enter credentials for every instance. The replacement
                will no longer receive ProVenue updates from Global Defaults.
              </AlertDescription>
            </div>
            <div className="shrink-0">{trigger}</div>
          </div>
        </Alert>
      ) : trigger}
      <ListEditorSheet<ProVenueInstanceInput>
        open={open}
        onOpenChange={setOpen}
        title={isReplacingGlobalDefault ? "Replace ProVenue instances" : "ProVenue instances"}
        description={
          isReplacingGlobalDefault
            ? "Create the complete device-specific list. Every instance requires a consumer key and consumer secret. This device will no longer receive ProVenue instance changes from Global Defaults."
            : "Manage ProVenue instance connections used by Alfred."
        }
        itemNoun="instance"
        items={isReplacingGlobalDefault ? [] : items}
        newItem={emptyProVenueInstance}
        readOnly={disabled}
        getValidationError={(draft, newItemIndexes) => {
          if (isReplacingGlobalDefault && draft.length === 0) {
            return "Add at least one ProVenue instance to replace the global collection."
          }
          const missingCredentials = draft.some((item, index) =>
            (allCredentialsRequired || newItemIndexes.has(index)) &&
            (!item.consumerKey?.trim() || !item.consumerSecret?.trim()),
          )
          if (!missingCredentials) return undefined
          return allCredentialsRequired
            ? "Enter a consumer key and consumer secret for every ProVenue instance."
            : "Enter a consumer key and consumer secret for every new ProVenue instance."
        }}
        renderForm={(item, update, _index, readOnly, isNew) => {
          const credentialsRequired = allCredentialsRequired || isNew
          return (
          <>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Name</Label>
              <Input
                value={item.name}
                disabled={readOnly}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="e.g. Primary"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Host name</Label>
              <Input
                value={item.hostName}
                disabled={readOnly}
                onChange={(e) => update({ hostName: e.target.value })}
                placeholder="provenue.example.com"
                className={INPUT_CLASSES}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Consumer Key</Label>
              <MaskedCredentialInput
                aria-label="Consumer Key"
                value={readOnly ? PROTECTED_CREDENTIAL_MASK : item.consumerKey}
                disabled={readOnly}
                onChange={(e) => update({ consumerKey: e.target.value })}
                placeholder={
                  readOnly
                    ? undefined
                    : credentialsRequired
                      ? "Enter consumer key"
                      : "Leave blank to keep current"
                }
                required={!readOnly && credentialsRequired}
                aria-required={!readOnly && credentialsRequired}
                className={`${INPUT_CLASSES} placeholder:text-muted-foreground`}
              />
            </div>
            <div className="space-y-2">
              <Label className={LABEL_CLASSES}>Consumer Secret</Label>
              <MaskedCredentialInput
                aria-label="Consumer Secret"
                value={readOnly ? PROTECTED_CREDENTIAL_MASK : item.consumerSecret}
                disabled={readOnly}
                onChange={(e) => update({ consumerSecret: e.target.value })}
                placeholder={
                  readOnly
                    ? undefined
                    : credentialsRequired
                      ? "Enter consumer secret"
                      : "Leave blank to keep current"
                }
                required={!readOnly && credentialsRequired}
                aria-required={!readOnly && credentialsRequired}
                className={`${INPUT_CLASSES} placeholder:text-muted-foreground`}
              />
              {!readOnly && (
                  <p className="text-xs text-muted-foreground">
                    {credentialsRequired
                      ? "A consumer key and consumer secret are required for every new instance."
                      : "Leave blank to keep current. To change them, enter a new consumer key and consumer secret."}
                  </p>
              )}
            </div>
          </>
          )
        }}
        onSubmit={onChange}
      />
    </div>
  )
}
